/**
 * Alerts.
 *
 * The pricing page sells "3 alerts" on free and "25" on Trader. The endpoint
 * had no cap at all until this screen was built, which is the same defect as an
 * enforced limit nobody advertised — the number has to mean something in both
 * directions. The cap and the count both come from the API, so this screen
 * cannot disagree with the gate.
 *
 * **The copy stays descriptive.** An alert is a condition the user set, so the
 * product may state that the condition occurred and nothing more. "ES crossed
 * 5400" is a fact. "ES crossed 5400 — consider going long" is investment advice
 * from a company that is not a registered adviser.
 */
import { useCallback, useEffect, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import {
  api, ApiError, fmtSince,
  type AlertRow, type AlertsResponse, type AlertTrigger,
} from '../lib/api.js';
import { Link } from '../lib/router.js';

export function AlertsScreen({ symbols }: { symbols: string[] }): JSX.Element {
  const [data, setData] = useState<AlertsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.alerts());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load alerts.');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function remove(id: string): Promise<void> {
    setBusy(id); setError(null);
    try {
      await api.deleteAlert(id);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not delete that alert.');
    } finally {
      setBusy(null);
    }
  }

  if (!data) {
    return <div className="scroll-area"><h2>Alerts</h2><p className="dim">{error ?? 'Loading…'}</p></div>;
  }

  const atLimit = data.limit !== null && data.alerts.length >= data.limit;

  return (
    <div className="scroll-area">
      <div className="section-head">
        <h2>Alerts</h2>
        <span className={`chip ${atLimit ? 'chip-caution' : 'chip-ok'}`}>
          {data.alerts.length}
          {data.limit === null ? ' · unlimited' : ` of ${data.limit}`}
        </span>
      </div>

      <p className="panel-body">
        AEGIS tells you when a condition you set occurs. It does not tell you what to do
        about it.
      </p>

      {error && <div className="form-error" role="alert">{error}</div>}

      {atLimit && (
        <div className="notice-bar" role="status">
          You are at your plan's limit of {data.limit}. Delete one to add another, or{' '}
          <Link to="/pricing">see plans</Link>.
        </div>
      )}

      <h3 className="panel-h">ACTIVE · {data.alerts.length}</h3>
      {data.alerts.length === 0 && (
        <p className="panel-empty">No alerts yet. Add one below.</p>
      )}

      <ul className="device-list">
        {data.alerts.map((a) => (
          <li key={a.id} className="device-row">
            <div className="device-main">
              <span className="device-label">{describe(a.trigger)}</span>
              <span className="device-meta mono">
                {a.repeating ? 'repeating' : 'once'} · cooldown {a.cooldownSec}s ·
                {' '}{a.lastFiredAt ? `last fired ${fmtSince(a.lastFiredAt)}` : 'never fired'}
              </span>
              {a.note && <span className="device-meta">{a.note}</span>}
            </div>
            <button
              className="btn btn-danger compact"
              onClick={() => void remove(a.id)}
              disabled={busy === a.id}
            >
              {busy === a.id ? 'Deleting…' : 'Delete'}
            </button>
          </li>
        ))}
      </ul>

      <NewAlertForm symbols={symbols} disabled={atLimit} onCreated={load} />
    </div>
  );
}

/**
 * A trigger, in words.
 *
 * Deliberately a statement of the condition, never of what to do about it. See
 * the file comment — this is the surface where directive language would creep
 * in most naturally.
 */
function describe(t: AlertTrigger): string {
  switch (t.kind) {
    case 'price':
      // "at or above", matching the words on the form. A list that says
      // "above" when the user chose "at or above" reads as a different alert
      // from the one they set.
      return `${t.symbol} trades at or ${t.op} ${t.price}`;
    case 'percent':
      return `${t.symbol} moves ${t.changePercent > 0 ? '+' : ''}${t.changePercent}% on the day`;
    case 'rule':
      return `A prop-firm rule reaches ${Math.round(t.utilisation * 100)}% of its limit`;
    case 'expiry':
      return `${t.symbol} is ${t.daysToFirstNotice} days from first notice`;
  }
}

function NewAlertForm({
  symbols, disabled, onCreated,
}: {
  symbols: string[];
  disabled: boolean;
  onCreated(): void;
}): JSX.Element {
  const [kind, setKind] = useState<AlertTrigger['kind']>('price');
  const [symbol, setSymbol] = useState(symbols[0] ?? 'ES');
  const [op, setOp] = useState<'above' | 'below'>('above');
  const [price, setPrice] = useState('');
  const [percent, setPercent] = useState('1');
  const [days, setDays] = useState('5');
  const [utilisation, setUtilisation] = useState('80');
  const [note, setNote] = useState('');
  const [repeating, setRepeating] = useState(false);
  const [cooldown, setCooldown] = useState('300');

  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  function buildTrigger(): AlertTrigger | { error: string } {
    switch (kind) {
      case 'price': {
        const n = Number(price);
        if (!Number.isFinite(n)) return { error: 'Enter a price.' };
        return { kind: 'price', symbol, op, price: n };
      }
      case 'percent': {
        const n = Number(percent);
        if (!Number.isFinite(n) || n === 0) return { error: 'Enter a percentage.' };
        return { kind: 'percent', symbol, changePercent: n };
      }
      case 'expiry': {
        const n = Number(days);
        if (!Number.isInteger(n) || n < 0) return { error: 'Enter a whole number of days.' };
        return { kind: 'expiry', symbol, daysToFirstNotice: n };
      }
      case 'rule': {
        const n = Number(utilisation);
        if (!Number.isFinite(n) || n < 0 || n > 100) return { error: 'Enter 0 to 100.' };
        return { kind: 'rule', accountId: '', utilisation: n / 100 };
      }
    }
  }

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (pending || disabled) return;
    setError(null);

    const trigger = buildTrigger();
    if ('error' in trigger) { setError(trigger.error); return; }

    setPending(true);
    try {
      await api.createAlert({
        trigger,
        repeating,
        cooldownSec: Math.max(10, Math.min(86_400, Number(cooldown) || 300)),
        note: note.trim(),
      });
      setPrice(''); setNote('');
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create the alert.');
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="settings-block">
      <h3 className="panel-h">NEW ALERT</h3>

      <form onSubmit={onSubmit} className="settings-form" noValidate>
        <label className="field">
          <span className="field-label">Tell me when</span>
          <select
            className="input select-wide" value={kind} disabled={disabled}
            onChange={(e) => setKind(e.target.value as AlertTrigger['kind'])}
          >
            <option value="price">A contract trades through a price</option>
            <option value="percent">A contract moves a percentage on the day</option>
            <option value="expiry">A contract nears first notice</option>
            <option value="rule">A prop-firm rule nears its limit</option>
          </select>
        </label>

        {kind !== 'rule' && (
          <label className="field">
            <span className="field-label">Contract</span>
            <select
              className="input select-wide" value={symbol} disabled={disabled}
              onChange={(e) => setSymbol(e.target.value)}
            >
              {symbols.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        )}

        {kind === 'price' && (
          <div className="field-row">
            <label className="field">
              <span className="field-label">Direction</span>
              <select
                className="input select-wide" value={op} disabled={disabled}
                onChange={(e) => setOp(e.target.value as 'above' | 'below')}
              >
                <option value="above">at or above</option>
                <option value="below">at or below</option>
              </select>
            </label>
            <label className="field">
              <span className="field-label">Price</span>
              <input
                className="input" type="number" step="any" inputMode="decimal"
                disabled={disabled} value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
            </label>
          </div>
        )}

        {kind === 'percent' && (
          <label className="field">
            <span className="field-label">Percent change</span>
            <input
              className="input" type="number" step="any" disabled={disabled}
              value={percent} onChange={(e) => setPercent(e.target.value)}
            />
            <span className="field-hint">Negative for a move down.</span>
          </label>
        )}

        {kind === 'expiry' && (
          <label className="field">
            <span className="field-label">Days before first notice</span>
            <input
              className="input" type="number" step="1" min="0" disabled={disabled}
              value={days} onChange={(e) => setDays(e.target.value)}
            />
            <span className="field-hint">
              First notice, not last trade — a physically delivered contract held past
              first notice can be assigned.
            </span>
          </label>
        )}

        {kind === 'rule' && (
          <label className="field">
            <span className="field-label">Percent of the limit used</span>
            <input
              className="input" type="number" step="1" min="0" max="100" disabled={disabled}
              value={utilisation} onChange={(e) => setUtilisation(e.target.value)}
            />
          </label>
        )}

        <div className="field-row">
          <label className="field">
            <span className="field-label">Cooldown (seconds)</span>
            <input
              className="input" type="number" step="10" min="10" max="86400" disabled={disabled}
              value={cooldown} onChange={(e) => setCooldown(e.target.value)}
            />
          </label>
          <label className="field">
            <span className="field-label">Note (optional)</span>
            <input
              className="input" type="text" maxLength={200} disabled={disabled}
              value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Why this level matters"
            />
          </label>
        </div>

        <label className="toggle">
          <input
            type="checkbox" checked={repeating} disabled={disabled}
            onChange={(e) => setRepeating(e.target.checked)}
          />
          Keep firing after the first time
        </label>

        {error && <div className="form-error" role="alert">{error}</div>}

        <button className="btn btn-primary" type="submit" disabled={pending || disabled}>
          {pending ? 'Creating…' : 'Create alert'}
        </button>
      </form>
    </section>
  );
}
